import { CORE_SETUP_LABELS, SETUP_STATUS_LABELS, type SetupRowStatus } from '../../app/terminology/setup';
import { FEATURES } from '../../app/terminology/features';
import { getComponentLabel } from '../../app/terminology/components';
import {
    Badge,
    Banner,
    BlockStack,
    Button,
    Collapsible,
    Icon,
    InlineStack,
    Layout,
    Page,
    Select,
    Spinner,
    Text,
} from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import { SetupHealth, SetupThemeComponent } from "../../app/models/SetupHealth";
import { getSearchModeLabel } from "../../app/terminology/searchModes";
import { getSetupHealthPresentation } from "./setupHealthPresentation";
import { getCoreSetupSteps } from "./dashboardSetupStatus";

type HealthRowStatus = SetupRowStatus;

type HealthRowProps = {
    id: string;
    title: string;
    description: string;
    status: HealthRowStatus;
    detail?: string;
    actionLabel?: string;
    onAction?: () => void;
    actionUrl?: string;
};

type SetupGroupProps = {
    id: string;
    title: string;
    description: string;
    stepCount: number;
    children: ReactNode;
};

const healthStatusBadge = (status: HealthRowStatus) => {
    switch (status) {
        case "complete":
            return <Badge tone="success">{SETUP_STATUS_LABELS['complete']}</Badge>;
        case "action":
            return <Badge tone="attention">{SETUP_STATUS_LABELS['action']}</Badge>;
        case "optional":
            return <Badge tone="info">{SETUP_STATUS_LABELS['optional']}</Badge>;
        case "recommended":
            return <Badge>{SETUP_STATUS_LABELS['recommended']}</Badge>;
        case "not-applicable":
            return <Badge>{SETUP_STATUS_LABELS['not-applicable']}</Badge>;
        default:
            return <Badge tone="info">{SETUP_STATUS_LABELS['unverified']}</Badge>;
    }
};

const healthStatusSymbol = (status: HealthRowStatus) => {
    switch (status) {
        case "complete":
            return "✓";
        case "action":
            return "!";
        case "unverified":
            return "i";
        default:
            return "";
    }
};

const HealthRow = ({
    id,
    title,
    description,
    status,
    detail,
    actionLabel,
    onAction,
    actionUrl,
}: HealthRowProps) => {
    const [open, setOpen] = useState(false);
    const contentId = `${id}-details`;

    return (
        <div className={`pf-setup-health-step pf-setup-health-step--${status}${open ? " pf-setup-health-step--open" : ""}`}>
            <button
                type="button"
                className="pf-setup-health-step__summary"
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen((current) => !current)}
            >
                <span className="pf-setup-health-step__status" aria-hidden="true">
                    {healthStatusSymbol(status)}
                </span>
                <span className="pf-setup-health-step__title">{title}</span>
                <span className="pf-setup-health-step__badge">{healthStatusBadge(status)}</span>
                <span className="pf-setup-health-step__chevron" aria-hidden="true">
                    <Icon source={open ? ChevronUpIcon : ChevronDownIcon} />
                </span>
            </button>
            <Collapsible id={contentId} open={open} transition={{ duration: "150ms", timingFunction: "ease" }}>
                <div className="pf-setup-health-step__details">
                    <div className="pf-setup-health-step__copy">
                        <Text as="p" variant="bodySm" tone="subdued">{description}</Text>
                        {detail ? <Text as="p" variant="bodySm" tone="subdued">{detail}</Text> : null}
                    </div>
                    {actionLabel && (onAction || actionUrl) ? (
                        <div className="pf-setup-health-step__action">
                            <Button
                                onClick={onAction}
                                url={actionUrl || undefined}
                                target={actionUrl ? "_blank" : undefined}
                            >
                                {actionLabel}
                            </Button>
                        </div>
                    ) : null}
                </div>
            </Collapsible>
        </div>
    );
};

const SetupGroup = ({ id, title, description, stepCount, children }: SetupGroupProps) => {
    const [open, setOpen] = useState(true);
    const contentId = `${id}-content`;

    return (
        <section className="pf-setup-health-group">
            <button
                type="button"
                className="pf-setup-health-group__header"
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen((current) => !current)}
            >
                <span className="pf-setup-health-group__heading">
                    <span className="pf-setup-health-group__title">{title}</span>
                    <span className="pf-setup-health-group__description">{description}</span>
                </span>
                <span className="pf-setup-health-group__count">{stepCount} steps</span>
                <span className="pf-setup-health-group__chevron" aria-hidden="true">
                    <Icon source={open ? ChevronUpIcon : ChevronDownIcon} />
                </span>
            </button>
            <Collapsible id={contentId} open={open} transition={{ duration: "180ms", timingFunction: "ease" }}>
                <div className="pf-setup-health-group__content">{children}</div>
            </Collapsible>
        </section>
    );
};

const getComponentStatus = (
    component: SetupThemeComponent,
    inspectionAvailable: boolean,
): HealthRowStatus => {
    if (!component.applicable) return "not-applicable";
    if (!inspectionAvailable || component.installed == null) return "unverified";
    if (component.installed) return "complete";
    return component.required ? "action" : "optional";
};

const SetupHealthPage = () => {
    const navigate = useNavigate();
    const [health, setHealth] = useState<SetupHealth>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const loadHealth = useCallback(async (themeId?: string) => {
        setLoading(true);
        setError(undefined);

        try {
            setHealth(await agent.Settings.setupHealth(themeId));
        } catch {
            // This endpoint does not identify merchant-safe errors; keep technical diagnostics out of the page.
            setError("Unable to check PartFits setup.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadHealth();
    }, [loadHealth]);

    const presentation = useMemo(
        () => health ? getSetupHealthPresentation(health.overallStatus) : undefined,
        [health],
    );
    const themeOptions = [
        { title: "Published theme", isPublished: true },
        { title: "Draft themes", isPublished: false },
    ].map((group) => ({
        title: group.title,
        options: (health?.themes ?? [])
            .filter((theme) => theme.isPublished === group.isPublished)
            .map((theme) => ({ label: theme.name, value: theme.id })),
    })).filter((group) => group.options.length > 0);
    const selectedThemeNeedsManualInstall = health?.themeInspectionAvailable === true
        && health.selectedTheme?.supportsJsonTemplates === false;
    const visibleThemeComponents = (health?.components ?? []).filter(
        (component) => component.key !== "product-results" || component.applicable,
    );
    const manualInstallSupportUrl = health?.selectedTheme
        ? `mailto:support@govedia.com?subject=${encodeURIComponent("PartFits theme installation assistance")}&body=${encodeURIComponent(
            `Shop: ${health.shopDomain}\nTheme: ${health.selectedTheme.name}\n\nPlease help us complete a manual PartFits installation for this Shopify theme.`,
        )}`
        : "mailto:support@govedia.com?subject=PartFits%20theme%20installation%20assistance";
    const coreCompleteCount = health
        ? getCoreSetupSteps(health).filter(step => step.status === 'complete').length
        : 0;

    return (
        <Page
            title={FEATURES.setup}
            backAction={{ content: FEATURES.settings, onAction: () => navigate('/settings') }}
            subtitle="Configure PartFits, check its installation on the selected Shopify theme, and recheck after theme changes."
            primaryAction={{
                content: "Recheck setup",
                loading,
                onAction: () => loadHealth(health?.selectedThemeId),
            }}
        >
            <Layout>
                <Layout.Section>
                    <BlockStack gap="500">
                        {error ? (
                            <Banner tone="critical" title="Setup could not be loaded">
                                <BlockStack gap="200">
                                    <Text as="p">{error}</Text>
                                    <InlineStack>
                                        <Button onClick={() => loadHealth(health?.selectedThemeId)}>Try again</Button>
                                    </InlineStack>
                                </BlockStack>
                            </Banner>
                        ) : null}

                        {!health && loading ? (
                            <div className="pf-setup-health-card pf-setup-health-loading">
                                <InlineStack gap="200" blockAlign="center" align="center">
                                    <Spinner size="small" accessibilityLabel="Checking PartFits setup" />
                                    <Text as="p" tone="subdued">Checking your data and Shopify theme…</Text>
                                </InlineStack>
                            </div>
                        ) : null}

                        {health && presentation ? (
                            <>
                                <section className={`pf-setup-health-summary pf-setup-health-summary--${presentation.tone}`}>
                                    <div className="pf-setup-health-summary__header">
                                        <span className="pf-setup-health-summary__icon" aria-hidden="true">
                                            {presentation.tone === "success" ? "✓" : presentation.tone === "warning" ? "!" : "i"}
                                        </span>
                                        <Text as="h2" variant="headingSm">{presentation.title}</Text>
                                    </div>
                                    <div className="pf-setup-health-summary__body">
                                        <div className="pf-setup-health-summary__copy">
                                            <Text as="p">{health.overallMessage}</Text>
                                            {health.selectedTheme && !health.selectedTheme.isPublished ? (
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    You’re checking the draft theme “{health.selectedTheme.name}”. Your published storefront may have a different setup.
                                                </Text>
                                            ) : null}
                                        </div>
                                        <InlineStack gap="200" wrap>
                                            {health.selectedTheme?.previewUrl ? (
                                                <Button url={health.selectedTheme.previewUrl} target="_blank">Preview storefront</Button>
                                            ) : null}
                                            {health.selectedTheme?.manageUrl ? (
                                                <Button url={health.selectedTheme.manageUrl} target="_blank">Open Theme Editor</Button>
                                            ) : null}
                                        </InlineStack>
                                    </div>
                                </section>

                                <section className="pf-setup-health-card">
                                    <SetupGroup
                                        id="core-setup"
                                        title="Core setup"
                                        description="Data and behavior PartFits needs before storefront components can work."
                                        stepCount={4}
                                    >
                                        <HealthRow
                                            id="configure-fitment-fields"
                                            title={CORE_SETUP_LABELS.fields}
                                            description="Choose the fields shoppers use to identify their vehicle or machine."
                                            detail={`${health.core.enabledFieldCount.toLocaleString()} visible selector fields configured`}
                                            status={health.core.enabledFieldCount > 0 ? "complete" : "action"}
                                            actionLabel="Manage fields"
                                            onAction={() => navigate("/settings/selector-fields")}
                                        />
                                        <HealthRow
                                            id="add-fitment-records"
                                            title={CORE_SETUP_LABELS.records}
                                            description="Fitment records provide the selectable vehicles or machines in the storefront search."
                                            detail={`${health.core.fitmentRecordCount.toLocaleString()} active fitment records${health.core.referenceDataEnabled ? " · reference data enabled" : ""}`}
                                            status={health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? "complete" : "action"}
                                            actionLabel={health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? "View records" : "Import data"}
                                            onAction={() => navigate(health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? "/vehicles" : "/imports")}
                                        />
                                        <HealthRow
                                            id="connect-products"
                                            title={CORE_SETUP_LABELS.products}
                                            description="Link products to the vehicles or machines they fit, or mark products as universal if no fitment match is needed."
                                            detail={`${health.core.productFitmentAssignmentCount.toLocaleString()} fitment assignments · ${health.core.externalProductLinkCount.toLocaleString()} external product links · ${health.core.universalProductCount.toLocaleString()} universal products · ${health.core.syncedProductCount.toLocaleString()} synced products`}
                                            status={health.core.productFitmentAssignmentCount > 0 || health.core.externalProductLinkCount > 0 || health.core.universalProductCount > 0 ? "complete" : "action"}
                                            actionLabel="Manage products"
                                            onAction={() => navigate("/products")}
                                        />
                                        <HealthRow
                                            id="configure-storefront"
                                            title={CORE_SETUP_LABELS.storefront}
                                            description="Choose how searches run and how matching products are displayed."
                                            detail={`Current result mode: ${getSearchModeLabel(health.renderingMode)}`}
                                            status={health.core.storefrontConfigured && health.core.storefrontEnabled ? "complete" : "action"}
                                            actionLabel="Open settings"
                                            onAction={() => navigate("/settings")}
                                        />
                                    </SetupGroup>

                                    {health.legacyScriptTags && <SetupGroup
                                        id="shopify-changes"
                                        title="Shopify changes"
                                        description="Platform changes that affect how PartFits loads on your storefront."
                                        stepCount={1}
                                    >
                                        <HealthRow
                                            id="legacy-script-tags"
                                            title="Legacy ScriptTags"
                                            description="Shopify stops loading storefront ScriptTags on March 1, 2027. Vehicle search must load from an app block on your published theme before then."
                                            detail={health.legacyScriptTags.status === "Found"
                                                ? `${health.legacyScriptTags.count.toLocaleString()} legacy ScriptTag${health.legacyScriptTags.count === 1 ? "" : "s"} still registered. Add the app block, test it, then contact PartFits support.`
                                                : health.legacyScriptTags.status === "None"
                                                    ? "No ScriptTags registered by this app. This doesn't confirm the app block is installed; see Theme setup."
                                                    : "PartFits couldn't check for ScriptTags. Recheck setup, and contact support if this continues."}
                                            status={health.legacyScriptTags.status === "Found" ? "action" : health.legacyScriptTags.status === "None" ? "complete" : "unverified"}
                                            actionLabel={health.legacyScriptTags.status === "Found" ? "Open theme editor" : undefined}
                                            actionUrl={health.legacyScriptTags.status === "Found" ? `https://${health.shopDomain}/admin/themes/current/editor` : undefined}
                                        />
                                    </SetupGroup>}

                                    <SetupGroup
                                        id="theme-setup"
                                        title="Theme setup"
                                        description="These steps apply to the selected Shopify theme."
                                        stepCount={visibleThemeComponents.length}
                                    >
                                        {themeOptions.length > 0 ? (
                                            <div className="pf-setup-health-theme-select">
                                                <div className="pf-setup-health-theme-select__copy">
                                                    <Text as="h3" variant="headingSm">Select theme</Text>
                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                        {health.selectedTheme
                                                            ? `${health.selectedTheme.name}${health.selectedTheme.isPublished ? " · Published theme" : " · Draft theme"}`
                                                            : "Choose the theme you want to verify."}
                                                    </Text>
                                                </div>
                                                <div className="pf-setup-health-theme-select__control">
                                                    <Select
                                                        label="Select theme"
                                                        labelHidden
                                                        options={themeOptions}
                                                        value={health.selectedThemeId}
                                                        disabled={loading}
                                                        onChange={(value) => loadHealth(value)}
                                                    />
                                                </div>
                                            </div>
                                        ) : null}

                                        {health.themeInspectionWarning ? (
                                            <div className="pf-setup-health-theme-notice">
                                                <Banner
                                                    tone={health.themeReadPermissionGranted === false ? "warning" : "info"}
                                                    title={health.themeReadPermissionGranted === false
                                                        ? "Theme read permission required"
                                                        : health.themeInspectionAvailable && !health.selectedTheme
                                                            ? "No theme found"
                                                            : "Theme setup could not be verified"}
                                                >
                                                    <Text as="p">{health.themeInspectionWarning}</Text>
                                                </Banner>
                                            </div>
                                        ) : null}

                                        {selectedThemeNeedsManualInstall ? (
                                            <div className="pf-setup-health-theme-notice">
                                                <Banner tone="warning" title="This theme may need a manual installation">
                                                    <BlockStack gap="300">
                                                        <Text as="p">
                                                            This theme may need PartFits installed directly in its code. Contact us for help, or use an Online Store 2.0 theme. We may ask for Shopify collaborator access before making changes you approve.
                                                        </Text>
                                                        <InlineStack>
                                                            <Button url={manualInstallSupportUrl}>Contact PartFits support</Button>
                                                        </InlineStack>
                                                    </BlockStack>
                                                </Banner>
                                            </div>
                                        ) : null}

                                        {visibleThemeComponents.map((component) => {
                                            const status = getComponentStatus(component, health.themeInspectionAvailable);
                                            const detail = selectedThemeNeedsManualInstall && component.applicable
                                                ? component.required
                                                    ? "Manual installation may be needed for this theme"
                                                    : "This component can be included during an assisted manual installation"
                                                : component.locations.length > 0
                                                ? `Found in ${component.locations.join(", ")}`
                                                : component.required
                                                    ? "Required for the current PartFits configuration"
                                                    : component.applicable
                                                        ? "Optional storefront component"
                                                        : "Not required by the current search result mode";
                                            const actionLabel = selectedThemeNeedsManualInstall
                                                ? undefined
                                                : component.installed
                                                    ? "Manage"
                                                    : component.actionUrl
                                                        ? "Add to theme"
                                                        : undefined;

                                            return (
                                                <HealthRow
                                                    key={component.key}
                                                    id={`theme-component-${component.key}`}
                                                    title={getComponentLabel(component.key, component.label)}
                                                    description={component.description}
                                                    detail={detail}
                                                    status={status}
                                                    actionLabel={actionLabel}
                                                    actionUrl={selectedThemeNeedsManualInstall
                                                        ? undefined
                                                        : component.installed
                                                            ? component.manageUrl
                                                            : component.actionUrl}
                                                />
                                            );
                                        })}

                                    </SetupGroup>

                                </section>

                                <div className="pf-setup-health-footnote">
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {coreCompleteCount} of 4 core steps complete. Recheck after publishing a theme or changing app blocks.
                                    </Text>
                                </div>
                            </>
                        ) : null}
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default SetupHealthPage;
